import { useEffect } from "react";
import { householdActions } from "../redux/household/household-slice";
import { useUniquePossibilities } from "./useUniquePossibilities";
import { formatDate } from "devextreme/localization";
import { useAppDispatch } from "./useAppDispatch";
import { uqActions } from "@/redux/uq/uq-slice";
import { npvActions } from "@/redux/npv/npv-slice";
import { HouseholdContact } from "@/types/types";

function generateHeaderContent(
  householdName: string | undefined,
  primaryContact?: HouseholdContact,
  secondaryBirthDate?: string,
  isSingle?: boolean
): string {
  if (!primaryContact)
    return `Missing primary contact. TCP may not work properly for the ${householdName} household.`;
  else if (!primaryContact?.birthDate)
    return `Missing birth date of primary contact. TCP may not work properly for the ${householdName} household.`;
  else if (!secondaryBirthDate && !isSingle)
    return `Missing birth date of secondary contact. TCP may not work properly for the ${householdName} household.`;
  return `For ${householdName ?? ""}, based on information furnished ${formatDate(new Date(), "longDate")}`;
}

export default function useHeaderChange(externalCode: string, title: string) {
  const dispatch = useAppDispatch();
  const {
    data: upData,
    isSuccess,
    isLoading: upLoading,
    isRefetching,
    error,
    isError,
  } = useUniquePossibilities(externalCode);

  useEffect(() => {
    if (!isSuccess) return;

    const household = upData?.household;
    if (!household) {
      dispatch(
        householdActions.setHeader({
          content: `No household found for Id: ${externalCode}.`,
          title,
          isError: true,
          isLoading: false,
        })
      );
      return;
    }

    dispatch(householdActions.setHousehold(household));
    dispatch(uqActions.setHousehold(household));

    const primaryContact = household.primaryContact;
    const secondaryContact = household.secondaryContact;

    const content = generateHeaderContent(
      household.name,
      primaryContact,
      secondaryContact?.birthDate,
      household.isSingle
    );
    const isError = Boolean(content?.includes("Missing"));

    dispatch(householdActions.setHeader({ content, title, isError }));

    if (upData.data != null) {
      let titlesToDispatch;
      let upSettings;
      if (typeof upData.data === "string") {
        const { titles, settings } = JSON.parse(upData.data);
        titlesToDispatch = titles;
        upSettings = settings;
      } else {
        titlesToDispatch = upData.data.titles;
        upSettings = upData.data.settings;
      }

      dispatch(npvActions.setTitles(titlesToDispatch));
      dispatch(uqActions.setSettings(upSettings));
    }

    return () => {
      dispatch(householdActions.setHeader({ content: "", title: "" }));
    };
  }, [dispatch, externalCode, isSuccess, title, upData]);

  return {
    upData,
    upLoading,
    upRefetching: isRefetching,
    upError: error,
    upIsError: isError,
    upLoaded: isSuccess,
    household: upData?.household,
  };
}
